import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, CheckCircle, Snowflake, WashingMachine, Utensils, Flame } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { BrandsSlider } from '../components/BrandsSlider';
import { Testimonials } from '../components/Testimonials';

const markhamSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Infinity Appliances Repair - Markham",
  "telephone": "+1-647-919-6893",
  "priceRange": "$$",
  "address": { "@type": "PostalAddress", "addressLocality": "Markham", "addressRegion": "ON", "addressCountry": "CA" },
  "areaServed": { "@type": "City", "name": "Markham" },
  "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "150" }
};

const services = [
  { icon: Snowflake, title: 'Refrigerator Repair', description: 'Expert fridge diagnostics and repair' },
  { icon: WashingMachine, title: 'Washer & Dryer', description: 'Same-day laundry appliance service' },
  { icon: Utensils, title: 'Dishwasher Repair', description: 'Fix leaks and drainage issues' },
  { icon: Flame, title: 'Stove & Oven', description: 'Get cooking again fast' },
];

export const MarkhamPage = () => {
  useEffect(() => {
    document.title = 'Markham Appliance Repair | 2-Hour Arrival Window | Infinity';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.name = 'description'; document.head.appendChild(metaDesc); }
    metaDesc.content = 'Professional appliance repair in Markham & Unionville. Experts in Bosch, Miele & KitchenAid. 2-hour arrival window. Call 647-919-6893.';
    let schemaScript = document.querySelector('#markham-schema');
    if (!schemaScript) { schemaScript = document.createElement('script'); schemaScript.id = 'markham-schema'; schemaScript.type = 'application/ld+json'; document.head.appendChild(schemaScript); }
    schemaScript.textContent = JSON.stringify(markhamSchema);
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="markham-page">
      <section className="hero-overlay relative min-h-[70vh] flex items-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&q=85)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="hero-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <nav className="mb-6 text-white/70 text-sm"><Link to="/" className="hover:text-white">Home</Link><span className="mx-2">/</span><span className="text-white">Markham</span></nav>
            <div className="trust-badge mb-8"><MapPin size={18} className="text-[#FF6B00]" /><span className="text-white font-semibold">Serving Markham & Unionville</span></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>Professional Markham Appliance Repair & Service</h1>
            <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-2xl">Serving the Unionville and Markham area with pride. Our technicians are experts in Bosch, Miele, and KitchenAid, providing Markham residents with white-glove repair service.</p>
            <a href="tel:647-919-6893" className="pulse-cta inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg text-white transition-transform hover:scale-105 shadow-xl" style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }} data-testid="markham-hero-cta"><Phone size={22} /><span>Call 647-919-6893</span></a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}>Markham's Trusted Appliance Experts</h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-6">Markham is home to many families with high-end kitchens and premium appliances. Our technicians are specially trained to service luxury brands like Miele, Sub-Zero, Wolf, and Thermador.</p>
              <p className="text-[#475569] leading-relaxed mb-8">We serve all Markham neighborhoods including Unionville, Markham Village, Cornell, and the areas along Highway 7. Our team provides white-glove service with 2-hour arrival windows.</p>
              <div className="space-y-4 mb-8">
                {['2-Hour Arrival in Markham', 'Bosch & Miele Specialists', 'KitchenAid Certified', 'Same-Day Repairs', 'Premium Service Standards'].map((feature) => (
                  <div key={feature} className="flex items-center gap-3"><CheckCircle size={20} style={{ color: '#FF6B00' }} /><span className="font-medium" style={{ color: '#1A2B4C' }}>{feature}</span></div>
                ))}
              </div>
              <a href="tel:647-919-6893" className="inline-flex items-center gap-3 px-6 py-3 rounded-full font-bold text-white transition-transform hover:scale-105" style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }} data-testid="markham-content-cta"><Phone size={18} /><span>Call for Markham Service</span></a>
            </div>
            <div>
              <div className="map-container rounded-2xl overflow-hidden shadow-xl">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d92078.23!2d-79.3376!3d43.8561!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d4d5e7c6ae13d7%3A0x59dd4d8e0a0a0a0a!2sMarkham%2C%20ON!5e0!3m2!1sen!2sca!4v1710000000014" width="100%" height="400" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Markham Service Area Map" data-testid="markham-map"></iframe>
              </div>
              <p className="text-center text-[#475569] mt-4 text-sm">Serving all Markham neighborhoods including Unionville, Markham Village, Cornell & Highway 7</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8F9FC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center mb-10" style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}>Our Markham Repair Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => { const IconComponent = service.icon; return (
              <Card key={service.title} className="glass-card border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(255, 107, 0, 0.1)' }}><IconComponent size={28} style={{ color: '#FF6B00' }} /></div>
                  <h3 className="font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}>{service.title}</h3>
                  <p className="text-sm text-[#475569]">{service.description}</p>
                </CardContent>
              </Card>
            ); })}
          </div>
        </div>
      </section>

      <BrandsSlider />
      <Testimonials />

      <section className="py-16" style={{ background: '#1A2B4C' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>Need Appliance Repair in Markham?</h2>
          <p className="text-white/70 mb-8 text-lg">Our Markham technicians are ready to help. Same-day service available.</p>
          <a href="tel:647-919-6893" className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg text-white transition-transform hover:scale-105" style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }} data-testid="markham-final-cta"><Phone size={22} /><span>Call 647-919-6893 Now</span></a>
        </div>
      </section>
    </main>
  );
};

export default MarkhamPage;
